package uk.co.nickthecoder.glok.backend

import uk.co.nickthecoder.glok.documentation.LogicalPixels
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.scene.MousePointer
import uk.co.nickthecoder.glok.util.Log
import uk.co.nickthecoder.glok.util.Matrix
import kotlin.reflect.KClass

/**
 * All drawing operations are done from here.
 * By having this as an interface, this lets us have different _backends_,
 * which leaves open the possibility of creating a multi-platform kotlin project
 * with a different [Backend] for the `js` platform for use in a browser.
 *
 * NOTE. [LoggingBackend] doesn't draw anything, but lets us create test cases
 * without setting up an OpenGL.
 *
 * ## Color Mask : Difference between GLBackend and WebGLBackend
 *
 * `GLBackend` renders directly to the screen buffer, but `WebGLBackend` renders to a frame buffer (the canvas).
 * This makes a big difference, because the screen buffer does not have an alpha channel.
 *
 * To account for this, `WebGLBackend` needs to change the `colorMask`; disabling the alpha channel for all operations
 * except [clear]. At the end of [drawView] the color mask is reset.
 * If you need to draw to a frame buffer during [drawView] (not recommended), then ensure you handle the colorMask
 * correctly depending on which backend is in play.
 *
 */
interface Backend {

    // region ===== View =====

    val viewMatrix: Matrix

    fun drawFrame(window: Window, block: () -> Unit)
    fun drawView(width: Float, height: Float, scale: Float, block: () -> Unit)
    fun setMousePointer(window: Window, cursor: MousePointer)

    /**
     * If you need to use OpenGL while Glok is rendering a scene, you must perform these actions within a [block].
     * This saves the OpenGL state, runs the block, and then restores the state again.
     */
    fun saveRestoreState(block: () -> Unit) {
        block()
    }

    // endregion View

    // region ==== Clipping and Transforms ====

    /**
     * Clip (scissor). The values are [LogicalPixels] measured from the TOP left of the window.
     * The clipped region is the intersection of the currently clipped region and the newly requested region.
     * @return false if the clipped region is empty (i.e. if nothing will be drawn due to this clipping).
     */
    fun beginClipping(left: Float, top: Float, width: Float, height: Float): Boolean
    fun endClipping()

    /**
     * A convenient way to call [beginClipping] and [endClipping], with a [block] of code in between.
     */
    fun clip(left: Float, top: Float, width: Float, height: Float, block: () -> Unit) {
        if (beginClipping(left, top, width, height)) {
            block()
            endClipping()
        }
    }

    fun clip(clip: Boolean, left: Float, top: Float, width: Float, height: Float, block: () -> Unit) {
        if (clip) {
            if (beginClipping(left, top, width, height)) {
                try {
                    block()
                } finally {
                    endClipping()
                }
            }
        } else {
            block()
        }
    }

    fun noClipping(block: () -> Unit)

    /**
     * Transforms the coordinates of all drawing calls within the [block] of code.
     * Used by the `Rotation` node and `ProxyNode`.
     */
    fun transform(transformation: Matrix, block: () -> Unit)
    fun transform(transformation: Matrix)
    fun clearTransform()

    // endregion Clipping and Transforms

    // region ===== Drawing =====

    /**
     * Draws a flot color over the entire viewport.
     * Used at the start of each redraw.
     *
     * NOTE. The `WebGLImplementation` changes the `colorMask` during this operation, with the final state being
     * `colorMask( true, true, true, false )` i.e. the alpha channel is disabled.
     */
    fun clear(color: Color)

    fun fillRect(left: Float, top: Float, right: Float, bottom: Float, color: Color)

    fun fillCircle(centerX: Float, centerY: Float, radius: Float, color: Color)
    fun fillQuarterCircle(centerX: Float, centerY: Float, cornerX: Float, cornerY: Float, color: Color, radius: Float)

    fun strokeInsideRect(
        left: Float, top: Float, right: Float, bottom: Float,
        thickness: Float,
        color: Color
    ) {
        strokeInsideRect(
            left, top, right, bottom,
            left + thickness, top + thickness, right - thickness, bottom - thickness,
            color
        )
    }

    fun strokeInsideRect(
        left: Float, top: Float, right: Float, bottom: Float,
        insideLeft: Float, insideTop: Float, insideRight: Float, insideBottom: Float,
        color: Color
    )

    fun strokeCircle(centerX: Float, centerY: Float, radius: Float, innerRadius: Float, color: Color)
    fun strokeQuarterCircle(
        centerX: Float, centerY: Float, cornerX: Float, cornerY: Float,
        color: Color, radius: Float, innerRadius: Float
    )

    /**
     * Draws the part of the [texture] defined by the rectangle [srcX], [srcY]  [width], [height]
     * to [destX], [destY] (which is where the top-left of the source corresponds to).
     */
    fun drawTexture(
        texture: Texture,
        srcX: Float, srcY: Float, width: Float, height: Float,
        destX: Float, destY: Float, destWidth: Float = width, destHeight: Float = height,
        modelMatrix: Matrix? = null
    )

    fun drawTintedTexture(
        texture: Texture,
        tint: Color,
        srcX: Float, srcY: Float, width: Float, height: Float,
        destX: Float, destY: Float, destWidth: Float = width, destHeight: Float = height,
        modelMatrix: Matrix? = null
    )

    /**
     * A gradient fill.
     * Each vertex of each triangle has a color.
     *
     * @param vertices 3 Vector2 per triangle
     * @param colors Each corner of the triangle has an associated color.
     *        The length of [colors] must be half the length of `triangles`.
     */
    fun gradient(vertices: List<Vector2>, colors: Array<Color>)

    fun hsvGradient(vertices: List<Vector2>, colors: Array<FloatArray>)

    /**
     * OpenGL is moe efficient when operations can be batched.
     * So rather than call [drawTexture] multiple times (with the same texture, tint, and modelMatrix),
     * batch them and call [TextureBatch.draw] instead.
     */
    fun batch(texture: Texture, tint: Color?, modelMatrix: Matrix?, block: TextureBatch.() -> Unit)

    // endregion Drawing

    // region ===== Factory methods =====

    fun createWindow(width: Int, height: Int): Window

    /**
     * Creates a texture filled with the given [color].
     */
    fun createTexture(name: String, width: Int, height: Int, pixels: IntArray): Texture

    /**
     * Creates a texture filled with the given [color].
     */
    fun createTexture(name: String, width: Int, height: Int, color: Color = Color.TRANSPARENT): Texture

    /**
     * For built-in resources. The textures from [Resources.loadTexture] will be available immediately.
     *
     * In a JVM context, the images are loaded using `ClassLoader.getResource`, using [klass]'s classloader
     * or the system classloader when [klass] is null.
     *
     * In a JS context, the images are expected to be found within the web page, using simple `img' tags,
     * whose `id` is the combination of [path] and the [Resources.loadTexture]'s `name` parameter.
     * [klass] is ignored.
     *
     * Note, no caching is performed, therefore loading the same image twice will load and create two independent
     * textures (a waste of time and memory).
     *
     */
    fun resources(path: String, klass: KClass<*>? = null): Resources

    /**
     * In a JVM context, [path] is the path of a file (which may be a relative path).
     * The textures are loaded immediately (unlike in a JS context, where there will be a delay).
     *
     * In a JS context, [path] is the start of a URL which serves the image, with [Resources.loadTexture]'s
     * `name` parameter completing the url.
     * e.g. `path` might be `/resources` and `name` is `myIcons.png`, and therefore the final url is
     * `/resources/myIcons.png`. Not that the server isn't specified, as this is assumed to be the same
     * web server which served the html and .js files.
     *
     * Given that these resources cannot be retrieved immediately, you may want to use [Texture.onLoaded]
     * to run code once the image has been loaded. However, in many/most cases this isn't necessary,
     * as a placeholder is returned, and the scene will be updated automatically when/if the image is loaded.
     * In particular, any `ImageViews` which directly use the (yet to be loaded) texture will automatically
     * update when the texture is loaded.
     *
     * Note, no caching is performed, therefore loading the same image twice will load and create two independent
     * textures (a waste of time and memory).
     */
    fun fileResources(path: String): Resources

    // endregion Resources

    // region ===== Other =====

    fun reportError(): Boolean = false

    /**
     * Called at the start of a view, in an attempt to ensure there are no side effects
     * which will screw up our rendering, or the application's.
     *
     * NOTE. In order to achieve consistency between GLBackend and WebGLBackend,
     * `colorMask( true, true, true, false )` is included. i.e. we disable the alpha channel.
     * This is because in WebGLBackend rendering occurs on a frame buffer (which has an alpha channel),
     * whereas GLBackend renders to the screen buffer (which does NOT have an alpha channel).
     *
     * Code that draws to other frame buffers should set `colorMask` appropriately. i.e. if they attempt to
     * clear directly (rather than using [Backend.clear]), then ensure that the alpha channel is enabled.
     */
    fun reset() {}

    fun pollEvents(timeoutSeconds: Double)

    fun monitorSize(): Pair<Int, Int>?

    companion object {

        fun beginLogging(log: Log) {
            backend = LoggingBackend(backend, log)
        }


        fun endLogging() {
            val current = backend
            if (current is LoggingBackend) {
                backend = current.inner
            }
        }

        fun log(log: Log, block: () -> Unit) {
            val oldBackend = backend
            backend = LoggingBackend(oldBackend, log)
            block()
            backend = oldBackend
        }

    }

    // endregion

}

/**
 * OpenGL is more efficient if you `batch` operations. [Backend.batch] lets us create such a batch.
 * Currently, the only operation is the rectangular [draw].
 *
 * This was first used for drawing glyphs of bitmap fonts, and later used by NinePatch.
 */
interface TextureBatch {
    fun draw(
        srcX: Float, srcY: Float, width: Float, height: Float,
        destX: Float, destY: Float, destWidth: Float = width, destHeight: Float = height,
    )
}

lateinit var backend: Backend
